import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation, OnChanges, SimpleChanges } from "@angular/core";
import { AgGridModule } from "ag-grid-angular";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { AgGridAngular } from "ag-grid-angular";
import type { GridReadyEvent, GridOptions, GridApi, IGetRowsParams, ColDef } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { NgSelectModule } from '@ng-select/ng-select';
import { CommonModule } from "@angular/common";
import { FormsModule } from '@angular/forms';
import { TextboxComponent } from '../../../shared/design-system/textbox/textbox.component';
import { TextboxConfig } from "../../design-system/textbox/textbox.modal";
import { ButtonComponent } from "../../design-system/button/button.component";
import { ButtonConfig } from "../../design-system/button/button.modal";
import { SegmentTabsComponent } from "../../design-system/segment-tabs/segment-tabs.component";
import { ICONS } from "../../../_helpers/icons.constants";
import { GridBulkActionButton, GridConfig, GridSegmentTab, GridBulkSelect } from "../../../_models/common/grid/grid-config";
import { Subscription } from "rxjs";
import { Claims } from "../../../_models/common/claims";
import { ExpenseStatusEnum } from "src/app/_models";
import { NRFCConfigModel } from "src/app/_models/NrFc/nrfcconfig-model";
import { Router } from "@angular/router";
import { CustomHeaderComponent } from '../controls/header-component/custom-header.component';
import { CheckBoxRendererComponent } from '../controls/checkbox-renderer/checkbox-renderer.component';
import { AmountRendererComponent } from "../controls/amount-renderer/amount-renderer.component";
import { ButtonsRendererComponent } from "../controls/buttons-renderer/buttons-renderer.component";
import { ApprovalDataRendererComponent } from "../controls/approval-data-renderer/approval-data-renderer.component";
import { BadgeRendererComponent } from "../controls/badge-renderer/badge-renderer.component";
import { BooleanTextRendererComponent } from "../controls/boolean-text-renderer/boolean-text-renderer.component";
import { DualButtonFunctionsComponent } from "../controls/dual-button-functions/dual-button-functions.component";
import { TextboxRendererComponent } from "../controls/textbox-renderer/textbox-renderer.component";
import { ToggleRendererComponent } from "../controls/toggle-renderer/toggle-renderer.component";
import { TextAndDescriptionRendererComponent } from "../controls/text-and-description-renderer/text-and-description-renderer.component";
import { TextboxAndIconRendererComponent } from "../controls/textbox-and-icon-renderer/textbox-and-icon-renderer.component";
import { StatusSupportingTextRendererComponent } from "../controls/status-supporting-text-renderer/status-supporting-text-renderer.component";
import { IconRendererComponent } from "../controls/icon-renderer/icon-renderer.component";
import { RadioRendererComponent } from "../controls/radio-renderer/radio-renderer.component";
import { ProgressBarRendererComponent } from "../controls/progress-bar-renderer/progress-bar-renderer.component";
import { StatusRenderComponent } from "../controls/status-renderer/status-renderer.component";
import { StatusAndActionRendererComponent } from "../controls/status-and-action-renderer/status-and-action-renderer.component";
import { StatusDataRenderComponent } from "../controls/status-data-renderer/status-data-renderer.component";
import { DateRendererComponent } from "../controls/date-renderer/date-renderer.component";
import { DateTimeRendererComponent } from "../controls/date-time-renderer/date-time-renderer.component";
import { CheckIconRendererComponent } from "../controls/check-icon-renderer/check-icon-renderer.component";
import { CardNumberRendererComponent } from "../controls/card-number-renderer/card-number-renderer.component";
import { DownloadRendererComponent } from "../controls/download-renderer/download-renderer.component";
import { PercentageRendererComponent } from "../controls/percentage-renderer/percentage-renderer.component";
import { DiscountRendererComponent } from "../controls/discount-renderer/discount-renderer.component";
import { LogIconRendererComponent } from "../controls/log-icon-renderer/log-icon-renderer.component";
import { PolicyAmountRendererComponent } from "../controls/policy-amount-renderer/policy-amount-renderer.component";
import { SafeTextRendererComponent } from "../controls/safe-text-renderer/safe-text-renderer.component";
import { ConditionalTextRendererComponent } from "../controls/conditional-text-renderer/conditional-text-renderer.component";
import { LinkRendererComponent } from "../controls/link-renderer/link-renderer.component";
import { PreApprovalTagRendererComponent } from "../controls/pre-approval-tag-renderer/pre-approval-tag-renderer.component";
import { ClickToSetupRendererComponent } from "../controls/click-to-setup-renderer/click-to-setup-renderer.component";
import { TooltipRenderComponent } from "../controls/tooltip-renderer/tooltip-renderer.component";
import { TextWithTooltipRendererComponent } from "../controls/text-tooltip-renderer/text-tooltip-renderer.component";
import { IconButtonsRendererComponent } from "../controls/icon-buttons-renderer/icon-buttons-renderer.component";
import { NgSelectRendererComponent } from "../controls/ng-select-renderer/ng-select-renderer.component";
import { TextboxTwoDecimalNumberComponent } from "../controls/textbox-two-decimal-number-renderer/textbox-two-decimal-number-renderer.component";
import { CategoryInputRendererComponent } from "../controls/category-input-renderer/category-input-renderer.component";
import { ProjectNameRendererComponent } from "../controls/project-name-renderer/project-name-renderer.component";
// Ported from the legacy grid controls, now local standalone copies (the legacy folder is being
import { PerkActionLinksRendererComponent } from "../controls/perk-action-links-renderer/perk-action-links-renderer.component";
// retired, so nothing here may import out of `shared/grid/`). Class names, selectors and registry keys
// are unchanged from the legacy originals, so legacy column definitions port across untouched.
// Needed by the unified claimant grids (advances / NR / reports / expense list).
import { ActionsRendererComponent } from "../controls/actions-renderer/actions-renderer.component";
import { ReceiptsIconRendererComponent } from "../controls/receipts-icon-renderer/receipts-icon-renderer.component";
import { ReceiptsTextRendererComponent } from "../controls/receipts-text-renderer/receipts-text-renderer.component";
import { FieldValueRendererComponent } from "../controls/field-value-renderer/field-value-renderer.component";
import { PreApprovalLinkRendererComponent } from "../controls/pre-approval-link-renderer/pre-approval-link-renderer.component";
import { ViewRendererComponent } from "../controls/view-renderer/view-renderer.component";
// Needed by the unified APPROVER grids (UC9): the claimant-name badges (policy violation, deputy,
// counter-approver, potential fraud, FM-edited), the Pre-Approval column, the drill-down chevron and
// the Company Name link.
import { PolicyNameRendererComponent } from "../controls/policy-name-renderer/policy-name-renderer.component";
import { PreApprovalRendererComponent } from "../controls/pre-approval-renderer/pre-approval-renderer.component";
import { ChevronRendererComponent } from "../controls/chevron-renderer/chevron-renderer.component";
import { FormattedTextRenderer } from "../controls/formatted-text-renderer/formatted-text-renderer.component";
// Surplus/Deficit on the NR fuel-compliance grid (UC7 Screen 73). Reads `text`/`class` off the ROW,
// not the column, so only one column per row can use it.
import { MultiColorTextRendererComponent } from "../controls/multi-color-text-renderer/multi-color-text-renderer.component";
import { FillAvailableHeightDirective } from "../../../_directives/fill-available-height.directive";


ModuleRegistry.registerModules([AllCommunityModule]);

interface IRow {
  make: string;
  model: string;
  price: number;
  electric: boolean;
}
@Component({
  standalone: true,
  imports: [AgGridAngular, TranslateModule, AgGridModule, NgSelectModule, CommonModule, FormsModule, TextboxComponent, ButtonComponent, SegmentTabsComponent, FillAvailableHeightDirective],
  selector: 'grid',
  templateUrl: './grid.component.html',
  encapsulation: ViewEncapsulation.None,


})
export class GridComponent implements OnInit, OnChanges {
  public ICONS = ICONS;
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["dropdownList"] && this.dropdownList && this.dropdownList.length) {
        this.selectedFilter = this.dropdownList[0]?.dropdownName;
    }
    if (changes["columnDefs"] && !this.gridOptions && this.columnDefs && this.columnDefs.length > 0) {
      this.initializeGrid();
    }
    if (changes["allGridData"] && this.gridApi) {
      if (this.gridConfigItems?.useClientSideRowModel) {
        // Client-side model: just hand the grid the new rows (autoHeight sizes them).
        this.gridApi.setGridOption('rowData', this.allGridData || []);
      } else {
        // Infinite model: force the active sort to re-apply to the freshly-loaded data.
        // Without this, sortAllRows() skips re-sorting when the sort column/order is unchanged.
        this.isRefresh = true;
        this.gridApi.setGridOption('datasource', this.getDataSource());
      }
    }
  }
  @Input("columns") columnDefs: any[] = [];
  @Input("data") allGridData: any;
  @Input("gridFilterItems") gridFilterItems: string;
  @Input("gridConfigItems") gridConfigItems: GridConfig;
  @Input("pinnedBottomRowData") pinnedBottomRowData: any;
  @Input("dataFilterItems") dataFilterItems: string;
  @Input("buttonFilterItem") buttonFilterItem: boolean = false;
  @Input("tagTextValue") tagTextValue: string;

  @Input() filterLabel: string = '';
  @Input() onFilterChangeFromParent?: (item: any) => void;
  @Input() dropdownList: any[] = [];

  @Input() customSearchFn?: (rows: any[], searchText: string) => any[];

  // --- Opt-in toolbar chrome. Both default to empty, so the 11 existing consumers render exactly
  // as before (combination 4 below: search only, right-aligned). ---
  @Input() bulkActions: GridBulkActionButton[] = [];  // toolbar shown only while rows are selected

  // Optional dropdown beside the bulk actions, for "apply THIS value to the selected rows". Two-way
  // bound so the host can read the chosen value when bulkActionClicked fires, and reset it after.
  @Input() bulkSelect?: GridBulkSelect;
  @Input() bulkSelectValue: any = null;
  @Output() bulkSelectValueChange = new EventEmitter<any>();
  /**
   * Bulk-bar caption, ALREADY TRANSLATED, e.g. `"{{count}} Expenses Selected"`.
   *
   * `{{count}}` is substituted with the live selection count wherever it appears — the same token and
   * the same parser the translator keys use, so a language that puts the number mid-sentence
   * (`已选择 {{count}} 笔交易`) needs no extra handling. A caption with no token renders verbatim,
   * which is a legitimate choice ("Items Selected"). Omit the input and the grid keeps its own
   * `transaction_selected` / `transactions_selected` pair, so no existing consumer changes.
   *
   * A plain string rather than a `(count) => string` callback: owner's call. The consequence to know
   * is that the CALLER cannot vary singular/plural, because it builds the string without knowing the
   * count — so pass a count-agnostic caption or accept one form throughout. Only the built-in
   * fallback keeps a singular/plural split.
   */
  @Input() bulkSelectedText?: string;
  @Input() bulkSelectedTextKey: string = "transaction_selected";
  @Input() bulkSelectedTextKeyPlural: string = "transactions_selected";
  @Input() bulkActionsMinSelection: number = 1;
  /** Compact sub-tabs for the toolbar's LEFT slot, e.g. Matched / Unmatched with receipts. */
  @Input() segmentTabs: GridSegmentTab[] = [];
  @Input() activeSegmentTab: any = null;
  /**
   * Current value of the opt-in date filter, as `yyyy-MM-dd` (what `<input type="date">` reads and
   * writes). Rendered only when `gridConfigItems.showDateFilter` is set.
   */
  @Input() dateFilterValue = "";

  /**
   * How the grid decides its own height. Opt-in: the default keeps today's behaviour exactly, where
   * the CONSUMER's wrapper class (`heightCalc-N`, `.grid-height_container`) supplies the height and
   * `.grid-container` fills it with `-webkit-fill-available`.
   *
   * - `"css"`    - as above. Every pre-existing consumer takes this branch and is untouched.
   * - `"fill"`   - the grid measures the space left below its own top edge and sets that as a pixel
   *                height (see `FillAvailableHeightDirective`). No height class needed, and no magic
   *                number to keep in sync with whatever chrome the screen renders above the grid.
   * - `"content"`- the grid is exactly as tall as its rows (ag-grid `domLayout: "autoHeight"`), for a
   *                short grid inside a panel or modal. Requires the client-side row model; see
   *                `resolvedHeightMode`.
   */
  @Input() heightMode: "css" | "fill" | "content" = "css";

  /**
   * `heightMode` after validation - what the template and `gridOptions` actually act on.
   *
   * ag-grid does not support `domLayout: "autoHeight"` with the infinite row model, which is this
   * component's default, so `"content"` without `gridConfigItems.useClientSideRowModel` would render
   * a zero-height grid. Resolved in `initializeGrid()`.
   */
  resolvedHeightMode: "css" | "fill" | "content" = "css";

  @Output() filterChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() searchTextChanged: EventEmitter<string> = new EventEmitter<string>();
  /**
   * The date filter changed, as `yyyy-MM-dd`. The grid does NOT filter its own rows on this — the
   * host re-fetches, because every screen needing it passes the date to the server.
   */
  @Output() dateFilterChanged = new EventEmitter<string>();

  @Output() onCellClick = new EventEmitter<any>();
  @Output() onCheckAll = new EventEmitter<boolean>();
  @Output() dataFilterChanged = new EventEmitter<any>();
  @Output() bulkActionClicked = new EventEmitter<string>();
  @Output() segmentTabSelected = new EventEmitter<any>();

  @ViewChild('searchTotalRow', { read: ElementRef }) searchElement: ElementRef<any>;

  gridWrapperstyle: any = {
    width: '100%',
    height: '100%',
  };
  rowClassRules;
  gridlastRowIndex: number;
  private isAllChecked: boolean = false;
  filters: any = null;
  param: string;
  resizeSubscription$: Subscription
  overlayLoadingTemplate: string;
  overlayNoRowsTemplate: string;
  isDataLoading: boolean = false;
  isRefresh: boolean = false;
  textNotFound: string = this.translate.instant('no_data_available');

  gridData: any;
  gridApi: GridApi;
  gridOptions: GridOptions;
  gridSortColumn: string;
  gridSortOrder: string;
  selectedFilter: any = null;
  selectedPeople: any[] = [];
  searchText: string = '';
  searchTextboxConfig: TextboxConfig = {
    label: "",
    type: "input",
    id: "fun-search",
    placeholder: "Search here",
    size: "md",
    iconSrc: this.ICONS.SEARCH,
    iconPosition: "left",
    value: this.searchText,
    required: false
  };
  currentDropdown = this.dropdownList && this.dropdownList.length ? this.dropdownList[0].dropdownName : null;
  people: any[] = [];

  ExpenseStatusEnum = ExpenseStatusEnum;
  showFilterPanel: boolean = false;
  claims: Claims;
  isContainerList: boolean = false;
  nrfcConfigModel: NRFCConfigModel = new NRFCConfigModel();
  constructor(private translate: TranslateService, private router: Router) {
    this.overlayNoRowsTemplate = `
    <div class="text-center noDataAvailable w-100pc f-16 position-relative p-0">
        ${this.translate.instant("no_data_availabe")}
      </div>
    `;
    this.overlayLoadingTemplate = `
    <div class="text-center noDataAvailable w-100pc f-16 position-relative p-0">
        ${this.translate.instant("data_loading")}
      </div>
    `;
    const currentUrl = this.router.url;
    this.isContainerList = currentUrl.includes('expensecontainerlist');
  }

  ngOnInit() {
    if (!this.gridConfigItems?.suppressAllFilterOption
      && this.dropdownList && !this.dropdownList.find(d => d.dropdownName === 'All' || d.filterKey === 'All')) {
      this.dropdownList.unshift({ dropdownName: 'All' });
    }
    if (!this.selectedFilter) {
      this.selectedFilter = this.currentDropdown ?? (this.dropdownList && this.dropdownList.length ? this.dropdownList[0].dropdownName : null);
    }
    this.handleFilterChange(this.selectedFilter);
    this.initializeGrid();
  }

  public refreshGridData() {
    this.overlayNoRowsTemplate = `
    <div class="text-center noDataAvailable w-100pc f-16 position-relative p-0">
        ${this.translate.instant(this.gridConfigItems.noDataMessage)}
      </div>
    `;
      this.initializeGrid();
      if (this.gridApi) {
          if (this.gridConfigItems?.useClientSideRowModel) {
              this.gridApi.setGridOption('rowData', this.allGridData || []);
          } else {
              // Re-apply the active sort to the (re)loaded data — see ngOnChanges note.
              this.isRefresh = true;
              this.resetDataSource();
          }
      }
      this.checkUncheckAll();
  }

  public setDataLoading(isDataLoading) {
    this.isDataLoading = isDataLoading;
    if (this.gridlastRowIndex > 0) {
      this.isDataLoading = false;
      this.gridApi.hideOverlay();
    }
    else if (this.isDataLoading) {
      this.gridApi.showLoadingOverlay();
    }
    else {
      this.gridApi.showNoRowsOverlay();
    }
  }

  public initializeGrid(isDataLoading: boolean = true) {
    let self = this;
    this.isDataLoading = isDataLoading;
    this.rowClassRules = this.fnBuildRowClassRules();
    // Resolved before the column guard below, so a grid whose columns arrive late still sizes itself.
    this.resolvedHeightMode = this.fnResolveHeightMode(!!this.gridConfigItems?.useClientSideRowModel);
    if (this.columnDefs && this.columnDefs.length > 0) {
      this.fnAddCheckboxColumn(this.columnDefs);

      this.overlayNoRowsTemplate = `
      <div class="text-center noDataAvailable w-100pc f-16 position-relative">
          ${this.translate.instant(this.gridConfigItems?.noDataMessage ?? 'no_data_availabe')}
        </div>
      `;

      this.fnPrepareColumnDefs();
      const clientSide = !!this.gridConfigItems?.useClientSideRowModel;
      this.gridOptions = {
        rowModelType: clientSide ? 'clientSide' : 'infinite',
        ...(clientSide ? { rowData: this.allGridData || [] } : { cacheBlockSize: 100, maxBlocksInCache: 3 }),
        // Default uniform row height (replaces the old hardcoded [rowHeight] template input).
        // In client-side mode, column autoHeight overrides this per row to fit content.
        rowHeight: 40,
        pagination: false,
        // Only for heightMode="content" - every other mode leaves ag-grid on its default
        // `domLayout: "normal"`, where the grid fills the container height instead of its rows.
        ...(this.resolvedHeightMode === "content" ? { domLayout: "autoHeight" as const } : {}),
        components: {
            cbxRenderer: CheckBoxRendererComponent,
            agColumnHeader: CustomHeaderComponent,
            amountRenderer: AmountRendererComponent,
            buttonsRenderer: ButtonsRendererComponent,
            approvalWithDataRenderer: ApprovalDataRendererComponent,
            BadgeRenderer: BadgeRendererComponent,
            booleanTextRenderer: BooleanTextRendererComponent,
            dualButtonFunctions: DualButtonFunctionsComponent,
            textboxRenderer: TextboxRendererComponent,
            toggleRenderer: ToggleRendererComponent,
            textAndDescriptionRenderer: TextAndDescriptionRendererComponent,
            textboxAndIconRenderer: TextboxAndIconRendererComponent,
            statusSupportingTextRenderer: StatusSupportingTextRendererComponent,
            iconRenderer: IconRendererComponent,
            radioRenderer: RadioRendererComponent,
            progressBarRenderer: ProgressBarRendererComponent,
            statusRenderer: StatusRenderComponent,
            statusDataRender: StatusDataRenderComponent,
            dateRenderer: DateRendererComponent,
            dateTimeRenderer: DateTimeRendererComponent,
            checkboxIconRenderer: CheckIconRendererComponent,
            cardNumberRenderer: CardNumberRendererComponent,
            downloadRenderer:DownloadRendererComponent,
            percentageRenderer: PercentageRendererComponent,
            discountRenderer: DiscountRendererComponent,
            logRenderer: LogIconRendererComponent,
            PolicyAmountRenderer: PolicyAmountRendererComponent,
            safeTextRenderer: SafeTextRendererComponent,
            conditionalTextRenderer: ConditionalTextRendererComponent,
            linkRenderer: LinkRendererComponent,
            preApprovalTagRenderer: PreApprovalTagRendererComponent,
            clickToSetupRenderer: ClickToSetupRendererComponent,
            customTooltip: TooltipRenderComponent,
            textWithTooltipRenderer: TextWithTooltipRendererComponent,
            iconButtonsRenderer: IconButtonsRendererComponent,
            ngSelectRenderer: NgSelectRendererComponent,
            textboxTwoDecimalNumberRenderer: TextboxTwoDecimalNumberComponent,
            categoryInputRenderer: CategoryInputRendererComponent,
            projectNameRenderer: ProjectNameRendererComponent,
            statusAndActionRenderer: StatusAndActionRendererComponent,
            // Ported from the legacy grid controls for the unified claimant grids (same keys):
            perkActionLinksRenderer: PerkActionLinksRendererComponent,
            actionRenderer: ActionsRendererComponent,
            receiptsIconRenderer: ReceiptsIconRendererComponent,
            receiptsTextRenderer: ReceiptsTextRendererComponent,
            fieldValueRenderer: FieldValueRendererComponent,
            preApprovalLinkRenderer: PreApprovalLinkRendererComponent,
            viewRenderer: ViewRendererComponent,
            // Approver grids (UC9). Keys match the legacy grid's registry so the legacy column
            // definitions port across unchanged.
            PolicyNameRenderer: PolicyNameRendererComponent,
            PreApprovalRenderer: PreApprovalRendererComponent,
            ChevronRenderer: ChevronRendererComponent,
            formattedTextRenderer: FormattedTextRenderer,
            multiColorTextRenderer: MultiColorTextRendererComponent
        },
        defaultColDef: {
          flex: 1,
          sortable: true,
          resizable: true,
        filter: 'agTextColumnFilter',
          // Off by default (uniform rows). A column opts in with `autoHeight: true` to size rows
          // to its content; the global `.ag-cell-wrapper{height:100%}` rule is relaxed for
          // `.ag-cell-auto-height` cells in _ag-grid.scss so the measurement works.
          autoHeight: true
          },
          tooltipShowDelay: 0,
        columnDefs: this.columnDefs,
      };
      if (this.gridApi) {
        this.gridApi.setGridOption('columnDefs', []);
        this.gridApi.setGridOption('columnDefs', this.columnDefs);
        this.gridApi.setGridOption('rowClassRules', this.rowClassRules);
        this.gridApi.setGridOption('datasource', this.getDataSource());
      }
    }
  }

  /**
   * Host-provided row class rules plus the two this component owns. Held in the `rowClassRules`
   * field the template binds, because ag-grid's own `[rowClassRules]` input overrides anything set
   * through `gridOptions`.
   *
   * `pb-total-pinned-row` carries the totals row's tint and bold text (`_ag-grid.scss`, scoped
   * inside `.grid-container` so it is new-grid-only). It is keyed on ag-grid's own `node.rowPinned`
   * rather than on a data flag, which is what makes it grid-wide: any screen that sets
   * `pinnedBottomRowData` gets the styling with no descriptor change, and a screen with no totals
   * row never matches. Unconditional for the same reason - there is nothing to opt into, a pinned
   * bottom row is a totals row on every grid using this component.
   *
   * `row-highlight` stays opt-in via `enableRowHighlight`.
   */
  private fnBuildRowClassRules(): any {
    return {
      ...(this.gridConfigItems?.rowClassRules || {}),
      'pb-total-pinned-row': (params: any) => params.node?.rowPinned === 'bottom',
      ...(this.gridConfigItems?.enableRowHighlight
        ? { 'row-highlight': (params: any) => !!params?.data?.isRowHighlighted }
        : {}),
    };
  }

  /**
   * Validates `heightMode` against the row model in use.
   *
   * `"content"` maps to ag-grid's `domLayout: "autoHeight"`, which ag-grid does not support with the
   * infinite row model - the grid would render with no height at all. This component defaults to
   * infinite, so a screen asking for `"content"` without `useClientSideRowModel` is a mistake worth
   * saying out loud rather than silently rendering nothing. Fall back to `"fill"`, which is the
   * closest thing that works.
   */
  private fnResolveHeightMode(clientSide: boolean): "css" | "fill" | "content" {
    if (this.heightMode === "content" && !clientSide) {
      console.warn(
        '<grid heightMode="content"> needs gridConfigItems.useClientSideRowModel - ag-grid cannot '
        + 'auto-size against the infinite row model. Falling back to heightMode="fill".'
      );
      return "fill";
    }
    return this.heightMode;
  }

  /**
   * Column-level defaults applied to every grid, so a screen's column definition carries only
   * what is genuinely its own.
   *
   * - `rowCount` is what the header component reads to draw "select all".
   * - An `iconButtonsRenderer` column is right-aligned: the row actions belong at the far edge
   *   of the row on every screen, so `<grid>` sets the header class instead of each column
   *   repeating it (the cell side is `justify-content` in `_ag-grid.scss`). A column that has
   *   already chosen an alignment keeps it - that is the `text-start` opt-out.
   */
  private fnPrepareColumnDefs() {
    const alignments = ['text-right', 'text-center', 'text-start', 'text-end'];
    this.columnDefs.forEach(columnDef => {
      if (!columnDef.headerComponentParams) {
        columnDef.headerComponentParams = {};
      }
      columnDef.headerComponentParams.rowCount = this.allGridData.length;

      // `headerClass` also accepts a function or an array; a column that went that far has
      // made its own decision, so only the plain-string (or absent) case is filled in.
      if (columnDef.cellRenderer === 'iconButtonsRenderer'
        && (columnDef.headerClass === undefined || typeof columnDef.headerClass === 'string')) {
        const headerClass = columnDef.headerClass || '';
        if (!alignments.some(a => headerClass.includes(a))) {
          columnDef.headerClass = `${headerClass} text-right`.trim();
        }
      }
    });
  }

  public fnAddCheckboxColumn(columnDefs) {
    let self = this;
    if (self.gridConfigItems?.enableRowSelection) {
      if (!this.columnDefs.find(c => c.field == 'selected')) {
        const cbxColumn = {
          headerName: "",
          field: "selected",
          cellRenderer: 'cbxRenderer',
          // No checkbox on the pinned totals row. `cellRenderer` alone renders unconditionally, and
          // `CheckBoxRendererComponent` reads only `data.isCheckboxDisabled` - which the totals rows
          // built in the descriptors do not set - so the totals row showed a live, clickable
          // checkbox that selects nothing.
          //
          // `{ component: null }`, NOT `undefined`. ag-grid treats a FALSY selector result as "no
          // selector" and falls straight back to `colDef.cellRenderer` - the checkbox - so returning
          // undefined here silently does nothing (`_getUserCompKeys`: `if (selectorRes) {...} else {
          // assignComp(defObject[name]) }`). A truthy result with a null `component` assigns no
          // component at all, and the cell falls through to its plain value, which for `selected` on
          // a totals row is empty. One place, every grid, no renderer change and no CSS.
          cellRendererSelector: (params: any) =>
            params.node?.rowPinned ? { component: null } : { component: 'cbxRenderer' },
          sortable: false,
          resizable: false,
          maxWidth: 60,
          pinned: true,
          headerClass: 'text-center',
          headerComponent: 'agColumnHeader',
          headerComponentParams: {
            isCheckbox: true,
            isAllChecked: false,
            isCheckAllAllowed: true,
            onCheckAll: (event) => this.onAllChecked(event.target.checked)
          },
          suppressMovable: true,
          lockPosition: true,
          lockPinned: true,
        };
        this.columnDefs.unshift(cbxColumn);
      }
    }
  }

  onAllChecked(isChecked: boolean) {
    // Check-all applies to what the user can actually SEE. With a search term active that is the
    // matching subset, not the whole set — previously the `allGridData` branch was skipped in that
    // case and the fallback read `this.gridData`, which is never assigned, so check-all silently
    // selected nothing after a search.
    // Opt-in via `checkAllRespectsSearch`, because fixing this changes which rows get selected: a
    // host that does not set it keeps the original behaviour exactly, search term or not.
    const rows = (this.gridConfigItems?.checkAllRespectsSearch && this.searchText)
      ? this.fnGetSearchMatchedRows()
      : (this.allGridData || []);
    for (let i = 0; i < rows.length; i++) {
      if (!rows[i].isEditDisabled && !rows[i].isCheckboxDisabled) {
        rows[i]['selected'] = isChecked;
      }
    }
    this.isAllChecked = isChecked;
    this.refreshCheckbox();
    this.checkUncheckAll();
    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
    }
    this.gridApi.redrawRows();
    this.onCheckAll.emit(isChecked);
  }

  checkUncheckAll() {
    if (this.gridApi && this.gridConfigItems?.enableRowSelection) {
      if (this.allGridData && this.allGridData.length > 0) {
        const selectableRows = this.allGridData.filter((row: any) => !row.isEditDisabled && !row.isCheckboxDisabled);
        const checkedCount = selectableRows.filter((row: any) => row.selected).length;
        this.isAllChecked = selectableRows.length > 0 && checkedCount === selectableRows.length;
      } else {
        this.isAllChecked = false;
      }
      this.refreshCheckbox();
    }
  }

  public checkItems(ids: any[]) {
    if (!this.allGridData || !this.gridConfigItems?.rowKey) return;
    const rowKey = this.gridConfigItems.rowKey;
    this.allGridData.forEach(row => {
      if (ids.includes(row[rowKey])) {
        row.selected = true;
      }
    });
    this.checkUncheckAll();
    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
      this.gridApi.redrawRows();
    }
  }

  public uncheckAll() {
    this.onAllChecked(false);
  }

  /** Selectively uncheck rows by their rowKey (symmetric to checkItems). */
  public uncheckItems(ids: any[]) {
    if (!this.allGridData || !this.gridConfigItems?.rowKey) return;
    const rowKey = this.gridConfigItems.rowKey;
    this.allGridData.forEach(row => {
      if (ids.includes(row[rowKey])) {
        row.selected = false;
      }
    });
    this.checkUncheckAll();
    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
      this.gridApi.redrawRows();
    }
  }

  /** Re-render after the host clears `isRowHighlighted` on rows (used by the highlight-then-fade UX). */
  public unsetRowHighlighted(rows: any[]) {
    if (rows) {
      this.allGridData = rows;
    }
    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
      this.gridApi.redrawRows();
    }
  }

  /** Set the pinned bottom (totals) row from the host descriptor. */
  public setPinnedBottomRow(rows: any[]) {
    this.pinnedBottomRowData = rows;
    if (this.gridApi) {
      this.gridApi.setGridOption('pinnedBottomRowData', rows);
    }
  }

  public refreshCheckbox() {
    if (this.gridApi) {
      if (this.gridConfigItems?.enableRowSelection) {
        var cbxColumn = this.columnDefs.find(c => c.field == 'selected');
        if (cbxColumn) {
          cbxColumn.headerComponentParams.isCheckAllAllowed = this.gridConfigItems?.isCheckAllAllowed ?? true;
          cbxColumn.headerComponentParams.isAllChecked = this.isAllChecked;
        }
        this.updateGridColumnDefinitions(null);
      }
    }
  }

  public updateGridColumnDefinitions(columnDefs: ColDef[]) {
    if (this.gridApi) {
      if (columnDefs && columnDefs.length > 0) {
        this.columnDefs = columnDefs;
        this.fnAddCheckboxColumn(columnDefs);
      }

      this.fnPrepareColumnDefs();

      //this.gridApi.setColumnDefs([]);
      this.gridApi.setGridOption('columnDefs', []);
      this.gridApi.setGridOption('columnDefs', this.columnDefs);


    }
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    // Per-row height: honour an explicit `gridRowHeight` if a consumer sets one; otherwise return
    // undefined so the grid decides � column `autoHeight` measures content (e.g. stacked note
    // chips), and rows without an auto-height column fall back to the `rowHeight` option (40).
    this.gridApi.setGridOption('getRowHeight', (p: any) =>
      (p && p.data && p.data.gridRowHeight) ? p.data.gridRowHeight : undefined);
    if (this.gridConfigItems?.useClientSideRowModel) {
      this.gridApi.setGridOption('rowData', this.allGridData || []);
    } else {
      this.gridApi.setGridOption('datasource', this.getDataSource());
    }
    this.resetSortModel();
  }

  /**
   * The rows the search box currently matches — a case-insensitive substring match across every
   * value of every row. Returns all rows when there is no search term. Shared by the row datasource
   * and by check-all, so the header checkbox always acts on exactly what is on screen.
   */
  private fnGetSearchMatchedRows(): any[] {
    const data = this.allGridData || [];
    if (!this.searchText || this.searchText.trim() === '') {
      return data;
      }
      const lowerSearch = this.searchText.toLowerCase();
      let filtered = data;
      if (this.customSearchFn) {
          filtered = this.customSearchFn(
              this.allGridData,
              this.searchText
          );
      } else {
          filtered = data.filter((row: any) => Object.keys(row).some(key => {
              const index = row[key];
              return index !== null && index !== undefined && index.toString().toLowerCase().indexOf(lowerSearch) > -1;
          }));
      }
      return filtered;
  }

  private getDataSource() {
    return {
      getRows: (params: IGetRowsParams) => {
        this.sortAllRows(params);
        const filtered = this.fnGetSearchMatchedRows();
        const total = filtered.length;
        const rowsForBlock = filtered.slice(params.startRow, params.endRow);
        params.successCallback(rowsForBlock, total);
        if (total === 0) {
          setTimeout(() => this.gridApi?.showNoRowsOverlay());
        } else {
          this.gridApi?.hideOverlay();
        }
      }
    };
  }

  onCellClicked(params: any) {
      if (params.column.colId === 'selected') {
          if (params.data.isCheckboxDisabled) {
              return;
          }
      // A checkbox-disabled row must not become selected, but the host still needs to hear about
      // the click — the approver screens answer a tick on your OWN claimant row by opening the
      // assign-FM panel. Emitting is what lets `resolveCellAction` see it.
      if (!params.data.isCheckboxDisabled) {
          params.data.selected = params.event.target.checked;
          this.onCellClick.emit(params);
          this.checkUncheckAll();
          return;
      }
    }
    this.onCellClick.emit(params);
  }

  onGridSearchTextChange(value: string) {
    this.searchText = value ? value.trim() : '';
    if (this.gridConfigItems?.useClientSideRowModel) {
      if (this.gridApi) { this.gridApi.setGridOption('quickFilterText', value || ''); }
    } else {
      this.refreshData();
    }
    this.searchTextChanged.emit(value);
  }

  refreshData() {
    if (this.gridApi) {
      this.isDataLoading = false;
      if (this.gridlastRowIndex > 0) {
        this.gridApi.refreshInfiniteCache();
      }
      else {
        this.resetDataSource();
      }
    }
  }

  resetDataSource() {
    this.gridApi.setGridOption('datasource', this.getDataSource());
  }

  handleFilterChange(item: any) {
    if (this.onFilterChangeFromParent) {
      this.onFilterChangeFromParent(item);
    }
    this.filterChanged.emit(item);
  }

  /** Number of currently-selected rows (drives the bulk-action toolbar visibility + count). */
  get selectedRowCount(): number {
    return (this.allGridData || []).filter((r: any) => r?.selected).length;
  }

  onBulkActionClick(key: string) {
    this.bulkActionClicked.emit(key);
  }

  onBulkDeselectAll() {
    this.uncheckAll();
  }

  onClear() {
    const allValue = this.currentDropdown ?? (this.dropdownList && this.dropdownList.length ? this.dropdownList[0].dropdownName : null);
    this.selectedFilter = allValue;
    if (this.onFilterChangeFromParent) {
      this.onFilterChangeFromParent(allValue);
    }
    this.filterChanged.emit(allValue);
  }

  public resetSortModel() {
    if (!this.gridConfigItems) return;
    this.sortGrid(this.gridConfigItems.defaultSortColumn, this.gridConfigItems.defaultSortOrder);
  }

  public setSortModel(sortColumn: string, sortDirection: string) {
    this.sortGrid(sortColumn, sortDirection);
  }

  private sortGrid(sortColumn, sortDirection) {
    this.gridApi.applyColumnState({
      state: [
        {
          colId: sortColumn,
          sort: sortDirection
        }
      ],
      defaultState: { sort: null }
    });
  }

  private sortAllRows(params) {
    if (params.sortModel && params.sortModel.length > 0) {
      if (this.gridSortColumn != params.sortModel[0].colId
        || this.gridSortOrder != params.sortModel[0].sort
        || this.isRefresh) {
        params.startRow = 0;
        params.endRow = this.gridOptions.cacheBlockSize;
        this.gridSortColumn = params.sortModel[0].colId;
        this.gridSortOrder = params.sortModel[0].sort;
        this.allGridData = this.sortByKey(this.allGridData, this.gridSortColumn, this.gridSortOrder);
      }
    }
    else {
      if (!this.gridSortColumn || !this.gridSortOrder || this.isRefresh) {
        params.startRow = 0;
        params.endRow = this.gridOptions.cacheBlockSize;
        this.gridSortColumn = this.gridConfigItems?.defaultSortColumn;
        this.gridSortOrder = this.gridConfigItems?.defaultSortOrder;
        this.allGridData = this.sortByKey(this.allGridData, this.gridSortColumn, this.gridSortOrder);
      }
    }
    this.isRefresh = false;
  }

  private sortByKey(array, key, order) {
    let result;
    if (order == 'asc') {
      result = array.sort((a, b) => {
        let x = a[key]; let y = b[key];
        if (typeof x === 'string' || x instanceof String) {
          x = x ? x.toString().toLowerCase() : null;
          y = y ? y.toString().toLowerCase() : null;
        }
        return !x ? -1 : ((x < y) ? -1 : ((x > y) ? 1 : 0));
      });
    }
    else {
      result = array.sort((a, b) => {
        let x = a[key]; let y = b[key];
        if (typeof x === 'string' || x instanceof String) {
          x = x ? x.toString().toLowerCase() : null;
          y = y ? y.toString().toLowerCase() : null;
        }
        return !y ? -1 : ((x > y) ? -1 : ((x < y) ? 1 : 0));
      });
    }
    return result;
    }
    // --- Toolbar slots ---
    //
    // The toolbar row has exactly two slots, and four combinations decided entirely here so no host
    // can get them wrong:
    //
    //   1. segment tabs LEFT   + search RIGHT
    //   2. status dropdown LEFT + search RIGHT
    //   3. "N selected" LEFT   + bulk buttons RIGHT   (wins over 1 and 2)
    //   4. nothing LEFT        + search RIGHT
    //
    // Combination 4 relies on `.grid-container__search { margin-left: auto }` — with
    // `justify-content: space-between` a lone child would otherwise sit at the LEFT edge, and the
    // search box must not change position between screens.


    // All four combinations above are ENHANCED-ONLY. A grid that binds none of the new inputs gets
    // the toolbar this component has always rendered - see `isEnhancedToolbar`.

    /**
     * Whether this host has opted into the new toolbar.
     *
     * Derived rather than configured, so no existing consumer has to do anything: binding
     * `segmentTabs`, `bulkActions` or `showDateFilter` IS the opt-in. A host that binds none of them
     * keeps the original markup (the `@else` branch in `grid.component.html`) and the original CSS
     * (everything outside `.--is-enhanced` in `_ag-grid.scss`).
     *
     * Keep this test to exactly those three inputs. Widening it - to `showFilter`, say - would flip
     * the layout for grids that never asked for it, which is the one thing this gate exists to stop.
     */
    get isEnhancedToolbar(): boolean {
        return !!(this.segmentTabs?.length || this.bulkActions?.length || this.gridConfigItems?.showDateFilter);
    }

    /** Combination 3: the bulk row takes over both slots, hiding tabs, dropdown and search. */
    /** Chosen value reported to the host; also gates the action buttons when bulkSelect.required. */
    onBulkSelectValueChange(value: any): void {
        this.bulkSelectValue = value;
        this.bulkSelectValueChange.emit(value);
    }

    /** True when a required bulk dropdown has nothing chosen yet. */
    get isBulkSelectMissing(): boolean {
        return !!this.bulkSelect?.required
            && (this.bulkSelectValue === null || this.bulkSelectValue === undefined || this.bulkSelectValue === '');
    }

    get isBulkMode(): boolean {
        return !!this.bulkActions?.length
            && this.selectedRowCount >= Math.max(1, this.bulkActionsMinSelection);
    }

    /**
     * Whether anything occupies the left slot. Tabs, dropdown and date picker are NOT mutually
     * exclusive — a screen declaring several gets them all, side by side.
     */
    get hasLeftSlot(): boolean {
        return !!(this.segmentTabs?.length || this.gridConfigItems?.showFilter || this.gridConfigItems?.showDateFilter);
    }

    /**
     * Whether anything occupies the right slot. Gated so a screen with a dropdown but no search
     * doesn't render an empty 200px-wide box against the row's right edge.
     */
    get hasRightSlot(): boolean {
        return !!(this.isBulkMode || this.gridConfigItems?.showSearch);
    }

    /**
     * Whether to render the toolbar row at all. Bulk mode and segment tabs are included so a screen
     * that has actions but neither search nor dropdown still gets a toolbar.
     */
    get showToolbar(): boolean {
        return !!(
            this.gridConfigItems?.showSearch ||
            this.gridConfigItems?.showFilter ||
            this.gridConfigItems?.showDateFilter ||
            this.segmentTabs?.length ||
            this.isBulkMode
        );
    }

    onSegmentTabSelected(value: any) {
        this.segmentTabSelected.emit(value);
    }

    /** Opt-in date filter. Emits only; the host decides what to re-fetch. */
    onDateFilterChange(value: string) {
        this.dateFilterValue = value;
        this.dateFilterChanged.emit(value);
    }


    /**
     * `ButtonConfig`s for the bulk row, cached by identity.
     *
     * `ds-button` reacts in `ngOnChanges`, so handing it a fresh object literal from the template
     * would re-run that every change-detection pass. Keying on the fields that actually vary means
     * the same button gets the same reference until something about it really changes.
     */
    private readonly bulkButtonCache = new Map<string, ButtonConfig>();

    private cachedButton(cacheKey: string, build: () => ButtonConfig): ButtonConfig {
        let config = this.bulkButtonCache.get(cacheKey);
        if (!config) {
            config = build();
            this.bulkButtonCache.set(cacheKey, config);
        }
        return config;
    }

    bulkButtonInput(action: GridBulkActionButton): ButtonConfig {
        const disabled = !!action.disabled || this.isBulkSelectMissing;
        return this.cachedButton(`${action.key}|${action.label}|${action.color}|${disabled}`, () => ({
            label: action.label,
            ariaLabel: action.label,
            size: "sm",
            color: action.color || "primary",
            disabled,
        }));
    }

    /** Deselect All only means anything for a multi-row selection, so it stays disabled on one row. */
    get deselectAllButtonInput(): ButtonConfig {
        const disabled = this.selectedRowCount < 2;
        return this.cachedButton(`__deselect|${disabled}`, () => ({
            label: this.translate.instant("deselect_all"),
            ariaLabel: this.translate.instant("deselect_all"),
            size: "sm",
            color: "primary-tertiary",
            disabled,
        }));
    }

    /**
     * The bulk bar's caption. Host-supplied text wins; otherwise the built-in key pair.
     *
     * Interpolation goes through ngx-translate's OWN parser rather than a hand-rolled regex:
     * `TranslateService.parser` is public and `TranslateParser.interpolate` uses the same
     * `templateMatcher` that resolves `{{count}}` inside a translator value. So there is one token
     * convention in the app, and a caller can paste a raw translator value straight in.
     *
     * `interpolate` is typed `string | undefined`, hence the fall back to the untouched input.
     */
    get bulkSelectedLabel(): string {
        const count = this.selectedRowCount;
        if (!this.bulkSelectedText) {
            return this.translate.instant(
                count === 1 ? this.bulkSelectedTextKey : this.bulkSelectedTextKeyPlural,
                { count }
            );
        }
        return this.translate.parser.interpolate(this.bulkSelectedText, { count }) ?? this.bulkSelectedText;
    }

}
